// The only place this module talks to its read seam: the `kpi-integrity-read`
// service (Ketosource/kpi-integrity, `kpi_integrity/read_api.py`), reached through
// the same-origin `/kpi-integrity` Pages proxy (functions/kpi-integrity/[[path]].ts).
// The service checks the Access JWT and asks the portal's /v1/authz/check itself.
//
// The portal is not a backend (I-4; GTASK-172 retired the portal-hosted facade of
// debt D-C10-1). When C6 `kpi-read-api` exists (KPI v4 R2), BASE moves and nothing
// else here changes. No module recomputes a figure (KPI v4 I-7): every state here
// is C2's, every registry field C1's. This module formats.

import type { FreshnessMeta } from "@/lib/api";

export type { FreshnessMeta };

export type HealthState = "ok" | "drifting" | "dark" | "unknown";

export type Health = {
  state: HealthState;
  reason: string | null;
  checked_at: string | null;
  check_date: string | null;
};

export type Source = {
  source_id: string;
  source_key: string;
  name: string | null;
  source_system: string | null;
  canonical_locator: string | null;
  collector: string | null;
  owner_email: string | null;
  owner_state: string;
  cadence: string | null;
  status: string | null;
  zone: string | null;
  dependent_kpis: string[];
  health: Health;
  /** Start of the trailing dark run in C2's rows; set on dark rows only (list and detail). */
  dark_since?: string | null;
};

export type Check = {
  check_date: string;
  state: HealthState;
  reason: string | null;
  evidence_kind: string | null;
};

export type SourceDetail = Source & {
  checks: Check[];
  checks_readable: boolean;
  commitment: { available: boolean; back_by: string | null; reason: string | null };
  restated_periods: number | null;
  dependent_apps: string[] | null;
};

export type Enveloped<T> = { data: T; meta?: FreshnessMeta };
export type SourcesData = { health_readable: boolean; sources: Source[] };
const BASE = import.meta.env.VITE_KPI_INTEGRITY_API_BASE ?? "/kpi-integrity/v1/kpi_data_integrity";

export class IntegrityError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
  ) {
    super(`Data Integrity API ${status}`);
  }
}

async function safeBody(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    credentials: "include",
    ...init,
    headers: { Accept: "application/json", ...(init?.body ? { "Content-Type": "application/json" } : {}) },
  });
  if (!response.ok) throw new IntegrityError(response.status, await safeBody(response));
  return (await response.json()) as T;
}

export const sourcesKey = ["kpi_data_integrity", "sources"] as const;
export const sourceKey = (id: string) => ["kpi_data_integrity", "source", id] as const;

export const getSources = () => call<Enveloped<SourcesData>>("/sources");
export const getSource = (id: string) => call<Enveloped<SourceDetail>>(`/sources/${encodeURIComponent(id)}`);

/** The steward's command. `assigned_by` is not sent: the API takes the proven caller. */
export const assignOwner = (sourceKey: string, ownerEmail: string, assignmentRef: string) =>
  call<Enveloped<unknown>>(`/sources/${encodeURIComponent(sourceKey)}/owner`, {
    method: "PUT",
    body: JSON.stringify({ owner_email: ownerEmail, assignment_ref: assignmentRef }),
  });

/** What a failed save says. Always names what to do next (UX §7 forbidden list). */
export function saveMessage(error: unknown): string {
  if (error instanceof IntegrityError) {
    const detail = (error.body as { detail?: { error?: string } } | null)?.detail;
    if (detail?.error === "assign_not_configured") return "Not saved. Assigning opens once the registry write is connected.";
    if (error.status === 403) return "Not saved. Only the Integrity Steward can assign owners.";
    if (error.status === 422) return "Not saved. Enter a Ketosource email and the decision reference, then retry.";
  }
  return "Not saved. Retry.";
}
